import React, { useMemo, useState, useEffect } from "react";
import { HiCalendar } from "react-icons/hi2";
import BottomNavBar from "../components/layout/BottomNav";

import {
  HiWifi,
  HiClock,
  HiRefresh,
  HiCloud,
  HiChevronDown,
} from "react-icons/hi";
import {
  HiMagnifyingGlass,
  HiAdjustmentsHorizontal,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi2";
import { useAuth } from "../contexts/AuthContext";
import {
  useDayPostList,
  useDayPostSubmission,
  usePendingDayPostOperations,
  useDayPostHelpers,
} from "../hooks/useDayPostData";
import { useLocation } from "react-router-dom";
import { useFirebaseData } from "../hooks/useFirebaseData";
import { UserPublicData } from "../types/user";
import { DayPost, ActivityLog } from "../types/post";
import {
  calculateAndSavePeakScore,
  generateDateID,
  getDayPosts,
  addLogIDToDayPost,
} from "../services/dayPostService";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import {
  DayPostCard,
  AddLogModal,
  AddNoteLogForm,
  AddWellnessLogForm,
  AddPerformanceLogForm,
  AddStepLogForm,
  AddRecoveryLogForm,
  AddNiggleLogForm,
  AddHealthLogForm,
  DeleteConfirmDialog,
  LogType,
} from "../components/DayPost";
import TrainingLogForm from "../components/DayPost/training-log/TrainingLogForm";
import DeleteTrainingLogDialog from "../components/DayPost/training-log/DeleteTrainingLogDialog";
import PerformanceFlow from "../components/DayPost/performance/PerformanceFlow";
import { PerformanceFlowSeed } from "../components/DayPost/performance/performanceDraft";
import { TrainingLog } from "../types/post";
import {
  getResultsForTrainingLog,
  getTrainingLog,
} from "../services/dayPostService";
import { trainingTypeService } from "../services/trainingTypeService";
import { useUserPoints } from "../services/pointsService";
import Header from "../components/layout/Header";
import Loading from "../components/Loading";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../providers/SubscriptionProvider";
import ActivityLinkModal from "../components/dashboard/ActivityLinkModal";
const Dashboard = () => {
  const { authState, currentUser } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  // Admin viewing: support /feed?uid={otherUserId}
  const location = useLocation();
  const queryUid = useMemo(
    () => new URLSearchParams(location.search).get("uid"),
    [location.search],
  );

  // If viewing another user's feed, fetch their public profile once
  const { data: viewedUser } = useFirebaseData<UserPublicData>({
    collection: "users",
    docId: queryUid || "",
    enabled: !!queryUid,
    maxAge: 600,
    enableRealtimeUpdates: true,
    fallbackToCache: true,
  });

  // Use offline-first hooks following developer guidelines
  const {
    dayPosts,
    loading: dayPostsLoading,
    loadingMore,
    error: dayPostsError,
    isFromCache,
    isOffline,
    fetchDayPostsData,
    loadMoreDays,
  } = useDayPostList({
    userId: queryUid || undefined,
    disableCache: !!queryUid,
  });

  // Points management
  const { refreshPoints } = useUserPoints();

  const {
    submitNoteLog,
    submitWellnessLog,
    submitPerformanceLog,
    submitStepLog,
    submitRecoveryLog,
    submitNiggleLog,
    submitHealthLog,
    deleteNoteLogById,
    deleteTrainingLogById,
    deleteWellnessLogById,
    deletePerformanceLogById,
    deleteStepLogById,
    deleteRecoveryLogById,
    deleteNiggleLogById,
    deleteHealthLogById,
    updateNoteLogById,
    updateWellnessLogById,
    updatePerformanceLogById,
    updateStepLogById,
    updateRecoveryLogById,
    updateNiggleLogById,
    updateHealthLogById,
    submitting,
    error: submissionError,
    isOffline: submitOffline,
  } = useDayPostSubmission(async () => {
    // Fetch fresh data after successful submission
    await fetchDayPostsData(false);
  });

  // Pending operations queue management
  const {
    pendingItems,
    addPendingItem,
    syncing,
    isOffline: pendingOffline,
  } = usePendingDayPostOperations(async () => {
    // Refetch day post data after successful sync
    await fetchDayPostsData(false);
  });

  const { formatDate } = useDayPostHelpers();

  // Canonical training type names by ID so search matches new-style logs.
  const [trainingTypeNamesById, setTrainingTypeNamesById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    trainingTypeService
      .fetchTrainingTypes()
      .then((types) => {
        const map: Record<string, string> = {};
        types.forEach((t) => {
          if (t.id) map[t.id] = t.name;
        });
        setTrainingTypeNamesById(map);
      })
      .catch((err) => console.error("Error fetching training types:", err));
  }, []);

  // Validate custom date range (max 50 days)
  const validateDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
      setDateRangeError("");
      return true;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 50) {
      setDateRangeError("Date range cannot exceed 50 days");
      return false;
    }

    setDateRangeError("");
    return true;
  };

  // Generate date IDs based on date range filter
  const getFilteredDateIDs = (): string[] => {
    const today = new Date();
    const dateIDs: string[] = [];

    if (dateRange === "this week") {
      // Last 7 days including today, offset by weekOffset
      const startOffset = weekOffset * 7;
      for (let i = startOffset; i < startOffset + 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dateIDs.push(generateDateID(date));
      }
    } else if (dateRange === "last week") {
      // 7 days starting from 7 days ago
      for (let i = 7; i < 14; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dateIDs.push(generateDateID(date));
      }
    } else if (dateRange === "custom" && customStartDate && customEndDate) {
      // Custom date range
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const current = new Date(end); // Start from end date (newest first)

      while (current >= start) {
        dateIDs.push(generateDateID(current));
        current.setDate(current.getDate() - 1);
      }
    }

    return dateIDs;
  };

  // Create searchable string from a dayPost based on selected log type
  const createSearchableString = (
    dayPost: DayPost,
    logTypeFilter: string,
  ): string => {
    const searchParts: string[] = [];

    // Helper to add notes and tags from logs
    const addNotesAndTags = (logs: any[], includeSessionName = false) => {
      logs.forEach((log) => {
        if (log.notes) searchParts.push(log.notes);
        if (log.tag && Array.isArray(log.tag)) {
          searchParts.push(...log.tag);
        }
        // Canonical training type name (new logs), in addition to legacy tags
        if (log.trainingTypeID && trainingTypeNamesById[log.trainingTypeID]) {
          searchParts.push(trainingTypeNamesById[log.trainingTypeID]);
        }
        if (includeSessionName && log.sessionName) {
          searchParts.push(log.sessionName);
        }
        // Add session name and description for sessions
        if (log.name) searchParts.push(log.name);
        if (log.description) searchParts.push(log.description);
        // Add performance title for performance logs
        if (log.performanceTitle) searchParts.push(log.performanceTitle);
        if (log.performanceResult) searchParts.push(log.performanceResult);
        // Add recovery activities
        if (log.selectedRecovery && Array.isArray(log.selectedRecovery)) {
          searchParts.push(...log.selectedRecovery);
        }
      });
    };

    // Filter by log type or include all
    if (!logTypeFilter || logTypeFilter === "") {
      // Include all log types
      addNotesAndTags(dayPost.trainingLogs);
      addNotesAndTags(dayPost.recoveryLogs);
      addNotesAndTags(dayPost.performanceLogs);
      addNotesAndTags(dayPost.healthLogs);
      addNotesAndTags(dayPost.stepLogs);
      addNotesAndTags(dayPost.noteLogs);
      addNotesAndTags(dayPost.niggleLogs);
      addNotesAndTags(dayPost.wellnessLogs);
      addNotesAndTags(dayPost.femaleCycleLogs);
      addNotesAndTags(dayPost.activityLogs, true); // Include session names from activities
      addNotesAndTags(dayPost.sessions); // Include sessions
    } else {
      // Include only selected log type
      switch (logTypeFilter) {
        case "training":
          addNotesAndTags(dayPost.trainingLogs);
          break;
        case "recovery":
          addNotesAndTags(dayPost.recoveryLogs);
          break;
        case "performance":
          addNotesAndTags(dayPost.performanceLogs);
          break;
        case "health":
          addNotesAndTags(dayPost.healthLogs);
          break;
        case "notes":
          addNotesAndTags(dayPost.noteLogs);
          break;
      }
    }

    // Join all parts and convert to lowercase for case-insensitive search
    return searchParts.join(" ").toLowerCase();
  };

  // Modal states
  const [addLogModalOpen, setAddLogModalOpen] = useState(false);
  const [selectedDateID, setSelectedDateID] = useState<string>("");

  // Form states
  const [noteLogFormOpen, setNoteLogFormOpen] = useState(false);
  const [trainingLogFormOpen, setTrainingLogFormOpen] = useState(false);
  const [wellnessLogFormOpen, setWellnessLogFormOpen] = useState(false);
  const [performanceLogFormOpen, setPerformanceLogFormOpen] = useState(false);
  const [stepLogFormOpen, setStepLogFormOpen] = useState(false);
  const [recoveryLogFormOpen, setRecoveryLogFormOpen] = useState(false);
  const [niggleLogFormOpen, setNiggleLogFormOpen] = useState(false);
  const [healthLogFormOpen, setHealthLogFormOpen] = useState(false);

  // Performance flow (PerformanceLog.md §3): create with an optional seed, or
  // edit an existing performance training log (+ its results).
  const [performanceFlowOpen, setPerformanceFlowOpen] = useState(false);
  const [performanceFlowSeed, setPerformanceFlowSeed] = useState<
    PerformanceFlowSeed | undefined
  >(undefined);
  const [performanceFlowEditLog, setPerformanceFlowEditLog] = useState<
    (TrainingLog & { id: string }) | null
  >(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<{
    id: string;
    type: string;
    dateID: string;
  } | null>(null);
  // Linked results of the performance training log pending deletion.
  const [linkedResultCount, setLinkedResultCount] = useState(0);

  // Edit state
  const [editingLog, setEditingLog] = useState<{
    id: string;
    type: string;
    data: any;
    dateID: string;
  } | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("this week");
  const [customDate, setCustomDate] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLogType, setSelectedLogType] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [dateFilteredPosts, setDateFilteredPosts] = useState<DayPost[]>([]);
  const [loadingDateFilter, setLoadingDateFilter] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0); // Track how many weeks back we are
  const [searchAllTime, setSearchAllTime] = useState(false); // Track if searching all time
  const [allTimePosts, setAllTimePosts] = useState<DayPost[]>([]); // Store all time posts for search
  const [loadingAllTime, setLoadingAllTime] = useState(false); // Track loading state for all time search
  const [dailyInsightsByDate, setDailyInsightsByDate] = useState<
    Record<string, string>
  >({});
  const [showActivityLinkModal, setShowActivityLinkModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState < (ActivityLog & { id: string }) | null >(null);
const [selectedActivityDayPost, setSelectedActivityDayPost] = useState<DayPost | null>(null);
 

  // Fetch all day posts when user starts searching
  useEffect(() => {
    const fetchAllDayPosts = async () => {
      // Only fetch if user has entered a search query and we haven't already loaded all posts
      if (!searchQuery.trim() || allTimePosts.length > 0 || queryUid) {
        return;
      }

      const userId = currentUser?.uid;
      if (!userId) return;

      try {
        setLoadingAllTime(true);
        // console.log("[FEED] Fetching all day posts for search...");

        // Get all day post references
        const dayPostsRef = collection(db, "dayPosts", userId, "posts");
        const snapshot = await getDocs(dayPostsRef);

        // Extract all dateIDs
        const allDateIDs = snapshot.docs.map((doc) => doc.id);
        // console.log(`[FEED] Found ${allDateIDs.length} day posts`);

        // Fetch all day posts with their logs
        const posts = await getDayPosts(userId, allDateIDs);
        // console.log(`[FEED] Loaded ${posts.length} day posts for search`);

        setAllTimePosts(posts);
        setSearchAllTime(true);
      } catch (error) {
        console.error("Error fetching all day posts:", error);
      } finally {
        setLoadingAllTime(false);
      }
    };

    fetchAllDayPosts();
  }, [searchQuery, currentUser?.uid, queryUid, allTimePosts.length]);

  // Fetch day posts for the selected date range
  useEffect(() => {
    const fetchDateRangePosts = async () => {
      // Skip if viewing another user's feed
      if (queryUid) {
        setDateFilteredPosts([]);
        setHasActiveFilters(false);
        return;
      }

      // For "this week" with no offset, use the default dayPosts that are already loaded
      if (dateRange === "this week" && weekOffset === 0) {
        setDateFilteredPosts([]);
        setHasActiveFilters(false);
        return;
      }

      // Don't fetch if custom date range is selected but dates aren't set
      if (dateRange === "custom" && (!customStartDate || !customEndDate)) {
        setDateFilteredPosts([]);
        return;
      }

      // Don't fetch if there's a date range error
      if (dateRangeError) {
        return;
      }

      const userId = currentUser?.uid;
      if (!userId) return;

      try {
        setLoadingDateFilter(true);
        const dateIDs = getFilteredDateIDs();

        if (dateIDs.length === 0) {
          setDateFilteredPosts([]);
          setLoadingDateFilter(false);
          return;
        }

        if (process.env.NODE_ENV !== "production") {
          // console.log("[FEED] Fetching posts for date range:", {
          //   dateRange,
          //   weekOffset,
          //   dateIDs,
          //   count: dateIDs.length,
          // });
        }

        // Fetch posts for the specific date range from database
        const posts = await getDayPosts(userId, dateIDs);

        if (process.env.NODE_ENV !== "production") {
          // console.log("[FEED] Fetched posts:", posts.length);
        }

        setDateFilteredPosts(posts);
        setHasActiveFilters(true);
      } catch (error) {
        console.error("Error fetching date range posts:", error);
        setDateFilteredPosts([]);
        setHasActiveFilters(false);
      } finally {
        setLoadingDateFilter(false);
      }
    };

    fetchDateRangePosts();
  }, [
    dateRange,
    weekOffset,
    customStartDate,
    customEndDate,
    currentUser?.uid,
    queryUid,
    dateRangeError,
  ]);

  // Filter and sort dayPosts based on search criteria
  const filteredAndSortedDayPosts = useMemo(() => {
    // Determine which posts to filter
    let postsToFilter: DayPost[];

    if (searchQuery.trim() && searchAllTime && allTimePosts.length > 0) {
      // When searching, use all time posts
      postsToFilter = allTimePosts;
    } else if (hasActiveFilters && dateFilteredPosts.length > 0) {
      // Use date filtered posts if we have active date filters
      postsToFilter = dateFilteredPosts;
    } else {
      // Otherwise use default dayPosts
      postsToFilter = dayPosts;
    }

    // Redistribute performance logs to the day they were performed (performanceDate),
    // not the day they were logged. Only move into day posts that are already in the
    // visible window — never spawn synthetic day posts, otherwise out-of-window dates
    // (e.g. April) would appear in this week's feed.
    const postsByDateID = new Map<string, DayPost>();
    postsToFilter.forEach((post) => {
      postsByDateID.set(post.dateID, { ...post, performanceLogs: [] });
    });
    postsToFilter.forEach((post) => {
      post.performanceLogs.forEach((perfLog) => {
        const targetDateID = perfLog.performanceDate || post.dateID;
        const targetPost = postsByDateID.get(targetDateID);
        if (!targetPost) {
          // performanceDate falls outside the visible window — drop from this view.
          // It will appear on the correct day when that window is loaded.
          return;
        }
        targetPost.performanceLogs.push(perfLog);
      });
    });

    let filtered = Array.from(postsByDateID.values());

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((dayPost) => {
        const searchableString = createSearchableString(
          dayPost,
          selectedLogType,
        );
        return searchableString.includes(query);
      });
    }

    // Sort by selected order
    filtered.sort((a, b) => {
      if (sortOrder === "newest") {
        // Newest first (descending dateID)
        return b.dateID.localeCompare(a.dateID);
      } else if (sortOrder === "oldest") {
        // Oldest first (ascending dateID)
        return a.dateID.localeCompare(b.dateID);
      }
      return 0;
    });

    return filtered;
  }, [
    dayPosts,
    dateFilteredPosts,
    allTimePosts,
    searchQuery,
    selectedLogType,
    sortOrder,
    hasActiveFilters,
    searchAllTime,
  ]);

  // Fetch saved daily insights for currently visible day posts.
  useEffect(() => {
    const targetUserId = queryUid || currentUser?.uid;

    if (!targetUserId || filteredAndSortedDayPosts.length === 0) {
      setDailyInsightsByDate({});
      return;
    }

    let cancelled = false;

    const fetchDailyInsights = async () => {
      const entries = await Promise.all(
        filteredAndSortedDayPosts.map(async (dayPost) => {
          const insightDocRef = doc(
            db,
            "aiInsights",
            targetUserId,
            "insights",
            `${dayPost.dateID}_daily_Day`,
          );

          try {
            const insightDoc = await getDoc(insightDocRef);
            if (!insightDoc.exists()) {
              return null;
            }

            const insightData = insightDoc.data() as { insight?: string };
            const insightText = insightData?.insight?.trim();

            if (!insightText) {
              return null;
            }

            return [dayPost.dateID, insightText] as const;
          } catch (error) {
            if (process.env.NODE_ENV !== "production") {
              console.warn(
                `[FEED] Failed to load daily insight for ${dayPost.dateID}`,
                error,
              );
            }
            return null;
          }
        }),
      );

      if (cancelled) {
        return;
      }

      const insightMap = entries.reduce<Record<string, string>>(
        (acc, entry) => {
          if (entry) {
            const [dateID, insight] = entry;
            acc[dateID] = insight;
          }
          return acc;
        },
        {},
      );

      setDailyInsightsByDate(insightMap);
    };

    fetchDailyInsights();

    return () => {
      cancelled = true;
    };
  }, [filteredAndSortedDayPosts, queryUid, currentUser?.uid]);

 const renderDayPostWithInsight = (dayPost: DayPost) => (
    <React.Fragment key={dayPost.dateID}>
      <DayPostCard
        userId={queryUid || currentUser?.uid || ""}
        username={queryUid ? viewedUser?.username : authState.user?.username}
        imageUrl={queryUid ? viewedUser?.imageUrl : authState.user?.imageUrl}
        dayPost={dayPost}
        dailyInsight={dailyInsightsByDate[dayPost.dateID]}
        onAddLog={handleAddLog}
        onDeleteLog={handleDeleteLog}
        onEditLog={handleEditLog}
        onAddWellnessLog={handleDirectWellnessAdd}
        onOpenActivityLinkModal={handleOpenActivityLinkModal}
        onActivityDataChanged={() => fetchDayPostsData(false)}
      />
    </React.Fragment>
  );

  // Debug logging
  if (process.env.NODE_ENV !== "production") {
    // console.log("[FEED] State:", {
    //   dayPostsLoading,
    //   dayPostsLength: dayPosts.length,
    //   dateFilteredPostsLength: dateFilteredPosts.length,
    //   loadingDateFilter,
    //   showFilters,
    //   hasActiveFilters,
    //   dateRange,
    //   filteredAndSortedLength: filteredAndSortedDayPosts.length,
    //   pendingLength: pendingItems.length,
    //   isFromCache,
    //   isOffline,
    //   syncing,
    //   submitting,
    // });
  }

  // Handle opening add log modal
  const handleAddLog = (dateID: string) => {
    setSelectedDateID(dateID);
    setAddLogModalOpen(true);
  };

  // Deep link: /feed?date=YYYYMMDD&addLog=performance[&eventId=…]
  // opens the Performance flow pre-seeded (Schedule event sheet, Dashboard
  // "Events to log"). Handled once per URL, then cleared.
  const handledDeepLinkRef = React.useRef<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("addLog") !== "performance") return;
    if (handledDeepLinkRef.current === location.search) return;
    handledDeepLinkRef.current = location.search;
    const date = params.get("date");
    const dateID =
      date && /^\d{8}$/.test(date) ? date : generateDateID(new Date());
    const eventId = params.get("eventId") || undefined;
    setSelectedDateID(dateID);
    setPerformanceFlowEditLog(null);
    setPerformanceFlowSeed({ eventId });
    setPerformanceFlowOpen(true);
    // Drop the params so a refresh does not reopen the flow.
    const remaining = new URLSearchParams(location.search);
    ["addLog", "date", "eventId"].forEach((k) =>
      remaining.delete(k),
    );
    const next = remaining.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true },
    );
  }, [location.search, location.pathname, navigate]);
  const handleOpenActivityLinkModal = (
    activity: ActivityLog & { id: string },
    dayPost: DayPost,
  ) => {
    setSelectedActivity(activity);
    setSelectedActivityDayPost(dayPost);
    setShowActivityLinkModal(true);
  };

  const handleCloseActivityLinkModal = () => {
    setShowActivityLinkModal(false);
    setSelectedActivity(null);
    setSelectedActivityDayPost(null);
  };

  const handleActivityLinkSuccess = async () => {
    await fetchDayPostsData(false);
  };

  // Handle adding wellness log directly (bypassing modal)
  const handleDirectWellnessAdd = (dateID: string) => {
    setSelectedDateID(dateID);
    setWellnessLogFormOpen(true);
  };

  // Handle log type selection
  const handleSelectLogType = (logType: LogType) => {
    if (process.env.NODE_ENV === "development") {
      console.log("Selected log type:", logType);
      console.log("Selected dateID:", selectedDateID);
    }

    setAddLogModalOpen(false);

    switch (logType) {
      case "note":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening note log form");
        }
        setNoteLogFormOpen(true);
        break;
      case "training":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening training log form");
        }
        setTrainingLogFormOpen(true);
        break;
      case "wellness":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening wellness log form");
        }
        setWellnessLogFormOpen(true);
        break;
      case "performance":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening performance flow");
        }
        setPerformanceFlowEditLog(null);
        setPerformanceFlowSeed(undefined);
        setPerformanceFlowOpen(true);
        break;
      case "step":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening step log form");
        }
        setStepLogFormOpen(true);
        break;
      case "recovery":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening recovery log form");
        }
        setRecoveryLogFormOpen(true);
        break;
      case "niggle":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening niggle log form");
        }
        setNiggleLogFormOpen(true);
        break;
      case "health":
        if (process.env.NODE_ENV === "development") {
          console.log("Opening health log form");
        }
        setHealthLogFormOpen(true);
        break;
      default:
        console.error("Unknown log type:", logType);
    }
  };

  // Handle note log submit (for both add and edit)
  const handleNoteLogSubmit = async (notes: string, logDate?: string) => {
    try {
      const dateID = logDate || selectedDateID;
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting note log for dateID:", dateID);
        console.log("Note content:", notes);
      }

      await submitNoteLog(dateID, notes, addPendingItem);
      setNoteLogFormOpen(false);
      setSelectedDateID("");

      if (process.env.NODE_ENV === "development") {
        console.log("Note log submitted successfully");
      }
    } catch (error) {
      console.error("Failed to submit note log:", error);
    }
  };

  // Handle training log submit (for both add and edit)
  const handleWellnessLogSubmit = async (wellnessLog: any) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting wellness log for dateID:", selectedDateID);
        console.log("Wellness log data:", wellnessLog);
      }

      // Save the wellness log quickly and close the form. The peak score is
      // calculated in the background; when it finishes we refresh the feed
      // again so the updated peak score appears without making the user wait.
      await submitWellnessLog(
        selectedDateID,
        wellnessLog,
        addPendingItem,
        async () => {
          try {
            await fetchDayPostsData(false);
            await refreshPoints();
          } catch (bgErr) {
            console.error(
              "Failed to refresh feed after background peak score:",
              bgErr,
            );
          }
        },
      );
      setWellnessLogFormOpen(false);
      setSelectedDateID("");

      // Refresh points and the feed immediately so the newly logged wellness
      // is visible right away (peak score will fill in shortly).
      await refreshPoints();
      await fetchDayPostsData(false);

      if (process.env.NODE_ENV === "development") {
        console.log(
          "Wellness log submitted successfully, peak score calculating in background",
        );
      }
    } catch (error) {
      console.error("Failed to submit wellness log:", error);
    }
  };

  const handlePerformanceLogSubmit = async (
    performanceLog: any,
  ): Promise<string> => {
    try {
      const result = await submitPerformanceLog(
        performanceLog.performanceDate || selectedDateID,
        performanceLog,
        addPendingItem,
      );
      // Don't close form here - let post-submit step handle it
      return result?.logID || "";
    } catch (error) {
      console.error("Failed to submit performance log:", error);
      return "";
    }
  };

  const handleStepLogSubmit = async (stepLog: any) => {
    try {
      const { logDate, ...stepData } = stepLog || {};
      const dateID = logDate || selectedDateID;
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting step log for dateID:", dateID);
        console.log("Step log data:", stepData);
      }

      await submitStepLog(dateID, stepData, addPendingItem);
      setStepLogFormOpen(false);
      setSelectedDateID("");
    } catch (error) {
      console.error("Failed to submit step log:", error);
    }
  };

  const handleRecoveryLogSubmit = async (recoveryLog: any) => {
    try {
      const { logDate, ...recoveryData } = recoveryLog || {};
      await submitRecoveryLog(
        logDate || selectedDateID,
        recoveryData,
        addPendingItem,
      );
      setRecoveryLogFormOpen(false);
      setSelectedDateID("");
    } catch (error) {
      console.error("Failed to submit recovery log:", error);
    }
  };

  const handleNiggleLogSubmit = async (niggleLog: any) => {
    try {
      const { logDate, ...niggleData } = niggleLog || {};
      const dateID = logDate || selectedDateID;
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting niggle log for dateID:", dateID);
        console.log("Niggle log data:", niggleData);
      }

      await submitNiggleLog(dateID, niggleData, addPendingItem);
      setNiggleLogFormOpen(false);
      setSelectedDateID("");

      if (process.env.NODE_ENV === "development") {
        console.log("Niggle log submitted successfully");
      }
    } catch (error) {
      console.error("Failed to submit niggle log:", error);
    }
  };

  const handleHealthLogSubmit = async (healthLog: any) => {
    try {
      const { logDate, ...healthData } = healthLog || {};
      const dateID = logDate || selectedDateID;
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting health log for dateID:", dateID);
        console.log("Health log data:", healthData);
      }

      await submitHealthLog(dateID, healthData, addPendingItem);
      setHealthLogFormOpen(false);
      setSelectedDateID("");

      if (process.env.NODE_ENV === "development") {
        console.log("Health log submitted successfully");
      }
    } catch (error) {
      console.error("Failed to submit health log:", error);
    }
  };

  // Handle closing modals
  const handleCloseModals = () => {
    setAddLogModalOpen(false);
    setNoteLogFormOpen(false);
    setTrainingLogFormOpen(false);
    setWellnessLogFormOpen(false);
    setPerformanceLogFormOpen(false);
    setStepLogFormOpen(false);
    setRecoveryLogFormOpen(false);
    setNiggleLogFormOpen(false);
    setHealthLogFormOpen(false);
    setPerformanceFlowOpen(false);
    setPerformanceFlowSeed(undefined);
    setPerformanceFlowEditLog(null);
    setSelectedDateID("");
    setEditingLog(null);
  };

  // Handle delete log
  const handleDeleteLog = (logId: string, logType: string, dateID: string) => {
    setLogToDelete({ id: logId, type: logType, dateID });
    setLinkedResultCount(0);
    if (logType === "training") {
      const log = dayPosts
        .flatMap((post) => post.trainingLogs)
        .find((l) => l.id === logId);
      if (log?.performanceType) {
        // Count the results folded into this performance so the dialog can
        // ask "Also delete N linked results?" (PerformanceLog.md §4).
        const local = dayPosts
          .flatMap((post) => post.performanceLogs)
          .filter((p) => p.linkedTrainingLogID === logId).length;
        setLinkedResultCount(local);
        if (currentUser?.uid && !queryUid) {
          getResultsForTrainingLog(currentUser.uid, logId)
            .then((results) => setLinkedResultCount(results.length))
            .catch(() => {});
        }
      }
    }
    setDeleteDialogOpen(true);
  };

  // The training log pending deletion, so the dialog can ask about linked
  // sessions / activity data (plan decision 5).
  const trainingLogToDelete = useMemo(() => {
    if (!logToDelete || logToDelete.type !== "training") return null;
    return (
      dayPosts
        .flatMap((post) => post.trainingLogs)
        .find((log) => log.id === logToDelete.id) ?? null
    );
  }, [logToDelete, dayPosts]);

  // Handle confirm delete
  const handleConfirmDelete = async (
    deleteLinkedSessionsAndActivity: boolean = false,
    deleteLinkedResults: boolean = false,
  ) => {
    if (!logToDelete) return;

    try {
      switch (logToDelete.type) {
        case "note":
          await deleteNoteLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "training":
          await deleteTrainingLogById(
            logToDelete.id,
            logToDelete.dateID,
            deleteLinkedSessionsAndActivity,
            addPendingItem,
            deleteLinkedResults,
          );
          break;
        case "wellness":
          await deleteWellnessLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "performance":
          await deletePerformanceLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "step":
          await deleteStepLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "recovery":
          await deleteRecoveryLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "niggle":
          await deleteNiggleLogById(logToDelete.id, logToDelete.dateID);
          break;
        case "health":
          await deleteHealthLogById(logToDelete.id, logToDelete.dateID);
          break;
        default:
          console.error("Unknown log type for deletion:", logToDelete.type);
      }

      setDeleteDialogOpen(false);
      setLogToDelete(null);
    } catch (error) {
      console.error("Failed to delete log:", error);
    }
  };

  // Open the Performance flow in edit mode for a performance training log.
  const openPerformanceEdit = (log: TrainingLog & { id: string }) => {
    setPerformanceFlowEditLog(log);
    setPerformanceFlowSeed(undefined);
    setPerformanceFlowOpen(true);
  };

  // Handle edit log
  const handleEditLog = async (
    logId: string,
    logType: string,
    logData: any,
    dateID: string,
  ) => {
    setEditingLog({ id: logId, type: logType, data: logData, dateID });
    setSelectedDateID(dateID);

    switch (logType) {
      case "note":
        setNoteLogFormOpen(true);
        break;
      case "training":
        if (logData?.performanceType) {
          setEditingLog(null);
          openPerformanceEdit({ ...logData, id: logId });
          break;
        }
        setTrainingLogFormOpen(true);
        break;
      case "wellness":
        setWellnessLogFormOpen(true);
        break;
      case "performance": {
        // A result that belongs to a performance training log edits the whole
        // performance; legacy result-only logs keep the old form.
        const parentId: string | undefined = logData?.linkedTrainingLogID;
        if (parentId) {
          let parent =
            dayPosts
              .flatMap((post) => post.trainingLogs)
              .find((l) => l.id === parentId) ?? null;
          if (!parent && currentUser?.uid && !queryUid) {
            try {
              const fetched = await getTrainingLog(currentUser.uid, parentId);
              if (fetched) parent = { ...fetched, id: parentId };
            } catch {
              parent = null;
            }
          }
          if (parent?.performanceType) {
            setEditingLog(null);
            openPerformanceEdit(parent);
            break;
          }
        }
        setPerformanceLogFormOpen(true);
        break;
      }
      case "step":
        setStepLogFormOpen(true);
        break;
      case "recovery":
        setRecoveryLogFormOpen(true);
        break;
      case "niggle":
        setNiggleLogFormOpen(true);
        break;
      case "health":
        setHealthLogFormOpen(true);
        break;
      default:
        console.error("Unknown log type for editing:", logType);
    }
  };

  // Add handlers for edit submissions
  const handleAddNoteLogWrapper = async (notes: string, logDate?: string) => {
    if (editingLog?.type === "note") {
      await updateNoteLogById(editingLog.id, { notes });
    } else {
      await handleNoteLogSubmit(notes, logDate);
    }
  };

  const handleAddWellnessLogWrapper = async (wellnessLog: any) => {
    if (editingLog?.type === "wellness") {
      // Update existing wellness log
      await updateWellnessLogById(editingLog.id, wellnessLog);
      try {
        // Trigger peak score recalculation for this date
        const dateID = editingLog.dateID || selectedDateID;
        if (dateID) {
          const result = await calculateAndSavePeakScore(dateID);
          if (!result?.success) {
            console.warn("Peak score recalculation failed:", result?.error);
          }
        }
      } catch (e) {
        console.error(
          "Failed to recalculate peak score after wellness edit:",
          e,
        );
      }
      // Close modal and refresh data/points
      setWellnessLogFormOpen(false);
      setSelectedDateID("");
      await refreshPoints();
      await fetchDayPostsData(false);
    } else {
      await handleWellnessLogSubmit(wellnessLog);
    }
  };

  const handleAddPerformanceLogWrapper = async (
    performanceLog: any,
  ): Promise<string> => {
    if (editingLog?.type === "performance") {
      await updatePerformanceLogById(editingLog.id, performanceLog);
      return "";
    } else {
      return await handlePerformanceLogSubmit(performanceLog);
    }
  };

  const handleAddStepLogWrapper = async (stepLog: any) => {
    if (editingLog?.type === "step") {
      const stepData = { ...stepLog };
      delete stepData.logDate;
      await updateStepLogById(editingLog.id, stepData);
    } else {
      await handleStepLogSubmit(stepLog);
    }
  };

  const handleAddRecoveryLogWrapper = async (recoveryLog: any) => {
    if (editingLog?.type === "recovery") {
      const recoveryData = { ...recoveryLog };
      delete recoveryData.logDate;
      await updateRecoveryLogById(editingLog.id, recoveryData);
    } else {
      await handleRecoveryLogSubmit(recoveryLog);
    }
  };

  const handleAddNiggleLogWrapper = async (niggleLog: any) => {
    if (editingLog?.type === "niggle") {
      const niggleData = { ...niggleLog };
      delete niggleData.logDate;
      await updateNiggleLogById(editingLog.id, niggleData);
    } else {
      await handleNiggleLogSubmit(niggleLog);
    }
  };

  const handleAddHealthLogWrapper = async (healthLog: any) => {
    const dateID =
      healthLog?.logDate ||
      (editingLog?.type === "health" ? editingLog.dateID : "") ||
      selectedDateID;
    if (editingLog?.type === "health") {
      const healthData = { ...healthLog };
      delete healthData.logDate;
      await updateHealthLogById(editingLog.id, healthData);
    } else {
      await handleHealthLogSubmit(healthLog);
    }
    if (!dateID) return;
    void (async () => {
      try {
        const result = await calculateAndSavePeakScore(dateID);
        if (!result?.success) {
          console.warn("Peak score recalculation failed:", result?.error);
        }
        await fetchDayPostsData(false);
      } catch (e) {
        console.error(
          "Failed to recalculate peak score after health log:",
          e,
        );
      }
    })();
  };

  // Initial loading state
  if (dayPostsLoading && dayPosts.length === 0) {
    return (
      <div className="min-h-screen bg-base-200 flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center">
          <Loading size="md" text="Loading Feed..." />
        </div>
      </div>
    );
  }

  // Show cached data while loading fresh data
  if (dayPostsLoading && dayPosts.length > 0) {
    return (
      <div className="p-2">
        {/* Offline/Cache Status Indicators */}
        {(isOffline || submitOffline || pendingOffline) && (
          <div className="alert alert-warning mb-4">
            <HiWifi className="text-lg" />
            <span>
              You're currently offline. Your logs will be queued and submitted
              when you're back online.
              {pendingItems.length > 0 && ` (${pendingItems.length} pending)`}
            </span>
          </div>
        )}

        {/* Syncing Status */}
        {syncing && (
          <div className="alert alert-info mb-4">
            <HiRefresh className="text-lg animate-spin" />
            <div className="w-full">
              <p className="text-sm mb-2">Syncing pending logs...</p>
              <div className="w-full bg-base-300 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full animate-pulse"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Day Posts */}
        <div>{filteredAndSortedDayPosts.map(renderDayPostWithInsight)}</div>

        {/* See More Button */}
        <div className="flex justify-center mt-4 mb-4">
          <button
            onClick={() => loadMoreDays(7)}
            disabled={
              loadingMore || isOffline || (!isOffline && authState.loading)
            }
            className="btn btn-primary flex items-center gap-2 px-6 py-2 disabled:btn-disabled"
          >
            {loadingMore ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-base-100"></div>
            ) : (
              <HiChevronDown />
            )}
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!authState.isAuthenticated) {
    return (
      <div className="p-6 text-center">
        <h3 className="text-lg font-semibold text-red-600">
          Please log in to view your dashboard
        </h3>
      </div>
    );
  }

  // Error state (only show if no cached data available)
  if (dayPostsError && dayPosts.length === 0) {
    return (
      <div className="p-6">
        <div className="alert alert-error mb-4">
          <span>{dayPostsError.message}</span>
        </div>
        <p className="text-base-content/70">
          Please refresh the page or try again later.
        </p>
      </div>
    );
  }

  // Debug logging
  if (process.env.NODE_ENV === "development") {
    // console.log("Dashboard render - Form states:", {
    //   addLogModalOpen,
    //   noteLogFormOpen,
    //   trainingLogFormOpen,
    //   wellnessLogFormOpen,
    //   performanceLogFormOpen,
    //   stepLogFormOpen,
    //   recoveryLogFormOpen,
    //   niggleLogFormOpen,
    //   healthLogFormOpen,
    //   selectedDateID,
    // });
  }

  return (
    <div className="pt-24 w-full pb-32">
      <Header />
      <div className="max-w-3xl mx-auto sm:px-0 px-8">
        <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-base-content font-heading pb-6">
          Training Diary
        </h1>
        {/* Offline/Cache Status Indicators */}
        {(isOffline || submitOffline || pendingOffline) && (
          <div className="alert alert-warning mb-4">
            <HiWifi className="text-lg" />
            <span>
              You're currently offline. Your logs will be queued and submitted
              when you're back online.
              {pendingItems.length > 0 && ` (${pendingItems.length} pending)`}
            </span>
          </div>
        )}

        {/* Syncing Status */}
        {syncing && (
          <div className="alert alert-info mb-4">
            <HiRefresh className="text-lg animate-spin" />
            <div className="w-full">
              <p className="text-sm mb-2">Syncing pending logs...</p>
              <div className="w-full bg-base-300 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full animate-pulse"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Submission Error */}
        {submissionError && !submitting && (
          <div className="alert alert-error mb-4">
            <span>{submissionError}</span>
          </div>
        )}

        {/* Pending Operations Display */}
        {pendingItems.length > 0 && (
          <div className="mb-4">
            {pendingItems.map((item) => (
              <div
                key={item.id}
                className="alert alert-warning mb-2 bg-gradient-to-r from-orange-50 to-base-100 border-dashed border-orange-400"
              >
                <HiCloud className="text-lg" />
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">
                      Pending: {item.logType} log for {formatDate(item.dateID)}
                    </span>
                    <div className="badge badge-warning badge-outline">
                      {syncing ? (
                        <>
                          <HiRefresh className="mr-1 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <HiClock className="mr-1" />
                          Waiting for connection
                        </>
                      )}
                    </div>
                  </div>
                  {item.retryCount && item.retryCount > 0 && (
                    <span className="text-xs text-red-600">
                      Failed to send {item.retryCount} time(s). Will retry when
                      connection is stable.
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search & Filter Section */}
      <div className="max-w-3xl mx-auto">
        <div className="p-4 pt-0 pb-0 sm:px-0 px-8">
          {/* Search Bar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiMagnifyingGlass className="h-5 w-5 text-base-content/50" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block rounded-md w-full pl-10 pr-3 py-4 border font-grotesk border-base-300 leading-5 bg-base-100 placeholder-base-content/60 focus:outline-none focus:placeholder-base-content/50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search logs..."
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-md relative ${
                  showFilters
                    ? " text-base-content/70"
                    : hasActiveFilters ||
                        selectedLogType ||
                        sortOrder !== "newest"
                      ? "text-base-content/50"
                      : " text-base-content/70 hover:bg-base-200"
                }`}
              >
                <HiAdjustmentsHorizontal className="h-6 w-6" />
              </button>
              {showFilters && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setDateRange("this week");
                    setCustomDate("");
                    setCustomStartDate("");
                    setCustomEndDate("");
                    setDateRangeError("");
                    setSelectedLogType("");
                    setSortOrder("newest");
                    setDateFilteredPosts([]);
                    setHasActiveFilters(false);
                    setShowFilters(false);
                    setWeekOffset(0);
                    setAllTimePosts([]);
                    setSearchAllTime(false);
                  }}
                  className="p-2 rounded-md text-base-content/40 hover:bg-base-200 hover:text-base-content transition-colors"
                  title="Reset filters"
                >
                  <HiRefresh className="h-6 w-6" />
                </button>
              )}
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="border-t border-base-300 pt-4 px-4 space-y-3 border-b mb-2">
              {/* Date Filter */}
              <div>
                <label className="block text-xs font-medium text-base-content/70 mb-1.5">
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="block w-full px-3 py-2.5 text-sm border border-base-300 rounded-lg bg-base-100 text-base-content focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="this week">This Week</option>
                  <option value="last week">Last Week</option>
                  <option value="custom">Custom</option>
                </select>

                {/* Custom Date Selector - only show when 'custom' is selected */}
                {dateRange === "custom" && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-base-content/70 mb-1">
                        From
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <HiCalendar className="h-5 w-5 text-base-content/50" />
                        </div>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => {
                            setCustomStartDate(e.target.value);
                            validateDateRange(e.target.value, customEndDate);
                          }}
                          className="block w-full pl-10 pr-3 py-2 border border-base-300 rounded-md leading-5 bg-base-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-base-content/70 mb-1">
                        To
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <HiCalendar className="h-5 w-5 text-base-content/50" />
                        </div>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => {
                            setCustomEndDate(e.target.value);
                            validateDateRange(customStartDate, e.target.value);
                          }}
                          className="block w-full pl-10 pr-3 py-2 border border-base-300 rounded-md leading-5 bg-base-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    {dateRangeError && (
                      <p className="text-sm text-red-600 mt-1">
                        {dateRangeError}
                      </p>
                    )}
                    <p className="text-xs text-base-content/60">
                      Maximum 50 days
                    </p>
                  </div>
                )}
              </div>

              {/* Log Type Filter */}
              <div>
                <label className="block text-xs font-medium text-base-content/70 mb-1.5">
                  Log Type
                </label>
                <select
                  value={selectedLogType}
                  onChange={(e) => setSelectedLogType(e.target.value)}
                  className="block w-full px-3 py-2.5 text-sm border border-base-300 rounded-lg bg-base-100 text-base-content focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="">All Types</option>
                  <option value="training">Training</option>
                  <option value="recovery">Recovery</option>
                  <option value="performance">Performance</option>
                  <option value="health">Health</option>
                  <option value="notes">Notes</option>
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-xs font-medium text-base-content/70 mb-1.5">
                  Sort By
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="block w-full px-3 py-2.5 text-sm border border-base-300 rounded-lg bg-base-100 text-base-content focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setDateRange("this week");
                    setCustomDate("");
                    setCustomStartDate("");
                    setCustomEndDate("");
                    setDateRangeError("");
                    setSelectedLogType("");
                    setSortOrder("newest");
                    setDateFilteredPosts([]);
                    setHasActiveFilters(false);
                    setShowFilters(false);
                    setWeekOffset(0);
                    setAllTimePosts([]);
                    setSearchAllTime(false);
                  }}
                  className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between">
          <button
            onClick={() => {
              setDateRange("this week");
              setWeekOffset(weekOffset + 1);
            }}
            className="p-2 mb-4 px-4 rounded-md text-base-content/80 text-xs flex items-center gap-2"
          >
            <HiChevronLeft className="h-4 w-4" />
            Last Week
          </button>
          {weekOffset > 0 && (
            <button
              onClick={() => {
                setWeekOffset(Math.max(0, weekOffset - 1));
              }}
              className="p-2 mb-4 px-4 rounded-md  text-base-content/80 text-xs flex items-center gap-2"
            >
              Forward
              <HiChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Results Count */}
      {(searchQuery ||
        hasActiveFilters ||
        selectedLogType ||
        sortOrder !== "newest") && (
        <div className="max-w-3xl mx-auto px-4 py-2">
          {loadingDateFilter || loadingAllTime ? (
            <p className="text-sm text-base-content/70">
              {loadingAllTime
                ? "Loading all logs for search..."
                : "Loading filtered results..."}
            </p>
          ) : (
            <p className="text-sm text-base-content/70">
              Showing {filteredAndSortedDayPosts.length} of{" "}
              {searchAllTime && allTimePosts.length > 0
                ? allTimePosts.length
                : hasActiveFilters && dateFilteredPosts.length > 0
                  ? dateFilteredPosts.length
                  : dayPosts.length}{" "}
              days{" "}
              {searchAllTime &&
                allTimePosts.length > 0 &&
                "(searching all time)"}
            </p>
          )}
        </div>
      )}

      {/* Day Posts */}
      <div>
        {loadingDateFilter || loadingAllTime ? (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-base-content/70 mt-4">
                Loading posts for selected date range...
              </p>
            </div>
          </div>
        ) : filteredAndSortedDayPosts.length === 0 ? (
          <div className="max-w-3xl mx-auto p-8 text-center">
            <p className="text-base-content/60 text-lg mb-2">
              No results found
            </p>
            <p className="text-base-content/50 text-sm">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          filteredAndSortedDayPosts.map(renderDayPostWithInsight)
        )}
      </div>

      {/* See More Button - Only show when not using date filters */}
      {!hasActiveFilters && (
        <div>
          <div className="flex justify-center mt-4 mb-4">
            <button
              onClick={() => loadMoreDays(7)}
              disabled={
                loadingMore || isOffline || (!isOffline && authState.loading)
              }
              className="w-full max-w-3xl mx-auto text-base-content/50 rounded-md text-center flex justify-center items-center gap-2 px-6 py-4 disabled:btn-disabled font-semibold font-grotesk"
            >
              {loadingMore ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-base-100 "></div>
              ) : (
                <HiChevronDown />
              )}
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        </div>
      )}
      <BottomNavBar></BottomNavBar>

      {/* Add Log Modal */}
      <AddLogModal
        open={addLogModalOpen}
        onClose={handleCloseModals}
        onSelectLogType={handleSelectLogType}
        dateID={selectedDateID}
        existingWellnessLogs={
          dayPosts.find((dayPost) => dayPost.dateID === selectedDateID)
            ?.wellnessLogs || []
        }
        existingHealthLogs={
          dayPosts.find((dayPost) => dayPost.dateID === selectedDateID)
            ?.healthLogs || []
        }
        userData={authState.user}
      />

      {/* Log Forms */}
      <AddNoteLogForm
        open={noteLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddNoteLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "note"}
        initialNotes={editingLog?.type === "note" ? editingLog.data.notes : ""}
        logId={editingLog?.type === "note" ? editingLog.id : ""}
      />

      {/* Delete Confirmation Dialog. Training logs use the decision-5 dialog
          ("Delete the linked sessions and activity data too?"). */}
      {logToDelete?.type === "training" ? (
        <DeleteTrainingLogDialog
          open={deleteDialogOpen}
          log={trainingLogToDelete}
          linkedResultCount={linkedResultCount}
          isDeleting={submitting}
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      ) : (
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => handleConfirmDelete(false)}
          logType={logToDelete?.type || ""}
          isDeleting={submitting}
        />
      )}

      <TrainingLogForm
        open={trainingLogFormOpen}
        onClose={handleCloseModals}
        context={{ dateID: selectedDateID }}
        editMode={editingLog?.type === "training"}
        logId={editingLog?.type === "training" ? editingLog.id : undefined}
        initialData={
          editingLog?.type === "training" ? editingLog.data : undefined
        }
        onSaved={async () => {
          handleCloseModals();
          await fetchDayPostsData(false);
        }}
      />

      <AddWellnessLogForm
        open={wellnessLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddWellnessLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "wellness"}
        initialData={
          editingLog?.type === "wellness" ? editingLog.data : undefined
        }
        logId={editingLog?.type === "wellness" ? editingLog.id : undefined}
      />

      <AddPerformanceLogForm
        open={performanceLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddPerformanceLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "performance"}
        initialData={
          editingLog?.type === "performance" ? editingLog.data : undefined
        }
        logId={editingLog?.type === "performance" ? editingLog.id : undefined}
      />

      {/* Performance flow — Competition / Game Day / Testing (PerformanceLog.md §3) */}
      <PerformanceFlow
        open={performanceFlowOpen}
        onClose={handleCloseModals}
        dateID={selectedDateID || generateDateID(new Date())}
        seed={performanceFlowSeed}
        editMode={!!performanceFlowEditLog}
        initialTrainingLog={performanceFlowEditLog ?? undefined}
        onSaved={async () => {
          await fetchDayPostsData(false);
        }}
      />

      <AddStepLogForm
        open={stepLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddStepLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "step"}
        initialData={editingLog?.type === "step" ? editingLog.data : undefined}
        logId={editingLog?.type === "step" ? editingLog.id : undefined}
      />

      <AddRecoveryLogForm
        open={recoveryLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddRecoveryLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "recovery"}
        initialData={
          editingLog?.type === "recovery" ? editingLog.data : undefined
        }
        logId={editingLog?.type === "recovery" ? editingLog.id : undefined}
      />

      <AddNiggleLogForm
        open={niggleLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddNiggleLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "niggle"}
        initialData={
          editingLog?.type === "niggle" ? editingLog.data : undefined
        }
        logId={editingLog?.type === "niggle" ? editingLog.id : undefined}
      />

      <AddHealthLogForm
        open={healthLogFormOpen}
        onClose={handleCloseModals}
        onSubmit={handleAddHealthLogWrapper}
        dateID={selectedDateID}
        editMode={editingLog?.type === "health"}
        initialData={
          editingLog?.type === "health" ? editingLog.data : undefined
        }
        logId={editingLog?.type === "health" ? editingLog.id : undefined}
      />
      {/* Activity Link Modal */}
      {selectedActivity && selectedActivityDayPost && (
        <ActivityLinkModal
          isOpen={showActivityLinkModal}
          onClose={handleCloseActivityLinkModal}
          activity={selectedActivity}
          unlinkedTrainingLogs={selectedActivityDayPost.trainingLogs.filter(
            (t) => !t.linkedActivityLogID,
          )}
          dateID={selectedActivityDayPost.dateID}
          onSuccess={handleActivityLinkSuccess}
        />
      )}
    </div>
  );
};

export default Dashboard;
